import type { ReactElement } from 'react';
import { cn } from '@/lib/cn';

export type SegmentedOption<T extends string> = { label: string; value: T };

export type SegmentedProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  size?: 'sm' | 'md';
  className?: string;
};

export function Segmented<T extends string>({
  value, onChange, options, size = 'md', className,
}: SegmentedProps<T>): ReactElement {
  const h = size === 'sm' ? 'h-8' : 'h-9';
  return (
    <div
      className={cn(
        'inline-flex items-center bg-[var(--bg-soft)] p-0.5 rounded-[var(--r-sm)]',
        h,
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              'px-3 h-full rounded-[calc(var(--r-sm)-2px)] text-[12px] font-semibold transition-colors',
              selected
                ? 'bg-[var(--bg-surface)] text-[var(--ink)] shadow-[var(--shadow-1)]'
                : 'text-[var(--ink-3)] hover:text-[var(--ink)]',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
